import React, { useState, useEffect } from 'react';
import { useMount, useUnmount } from 'react-use';
import { TablePagination, withStyles } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import { filter, first } from 'lodash';
import is from 'is_js';

import theme from 'utils/theme';
import { ArrowLeftIcon, LoadingIcon } from 'icons';

export default function PaginationComponent({
    items,
    labelDisplayedRows,
    initialPage = 1,
    initialRowsPerPage = 10,
    options,
    type,
    loading,
    shouldResetComponent,
    onResetComponent,
    onChangePageCallback,
    onChangeRowsPerPageCallback,
}) {
    const [page, setPage] = useState(initialPage);
    const [pages, setPages] = useState(
        is.number(items) && items > 0 ? Math.ceil(items / initialRowsPerPage) : 1,
    );
    const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
    const [rowsPerPageOptions, setRowsPerPageOptions] = useState([
        { label: '5 / PAGE', value: 5 },
        { label: '10 / PAGE', value: 10 },
        { label: '15 / PAGE', value: 15 },
        { label: '30 / PAGE', value: 30 },
    ]);

    useEffect(() => {
        if (shouldResetComponent) {
            setPage(1);
            setRowsPerPage(10);
            onResetComponent && onResetComponent(false);
        }
    }, [shouldResetComponent]);

    useEffect(() => {
        setPages(Math.ceil(items / rowsPerPage));
    }, [rowsPerPage, items]);

    useEffect(() => {
        setPage(initialPage);
    }, [initialPage]);

    useEffect(() => {
        const newItems =
            is.number(items) && items > 0
                ? Math.ceil(items / (rowsPerPage || initialRowsPerPage))
                : 1;
        setPages(newItems);
    }, [items]);

    useMount(() => {
        if (is.array(options)) {
            const optionsAsNumber = filter(options, (el) => is.number(el));
            if (optionsAsNumber.length === options.length) {
                setRowsPerPage(first(options));
                setRowsPerPageOptions(options);
            }

            const optionsAsObject = filter(options, (el) => el.label && el.value);
            if (optionsAsObject.length === options.length) {
                setRowsPerPage(first(options).value);
                setRowsPerPageOptions(options);
            }
        }
    });

    useUnmount(() => {
        setRowsPerPage(10);
        setRowsPerPageOptions([
            { label: '5 / PAGE', value: 5 },
            { label: '10 / PAGE', value: 10 },
            { label: '15 / PAGE', value: 15 },
            { label: '30 / PAGE', value: 30 },
        ]);
    });

    const onChangePage = (event, newPage) => {
        setPage(newPage);
        onChangePageCallback && onChangePageCallback(newPage);
    };

    const onChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value));
        onChangeRowsPerPageCallback && onChangeRowsPerPageCallback(parseInt(event.target.value));

        setPage(1);
        onChangePageCallback && onChangePageCallback(1);
    };

    if (loading) {
        return (
            <div className="pagination">
                <LoadingIcon type="rect" rect={{ height: '84px', width: '100%' }} />
            </div>
        );
    }

    return (
        <div className="pagination">
            <StyledPagination
                component="div"
                count={pages || 1}
                page={page}
                siblingCount={0}
                boundaryCount={1}
                onChange={onChangePage}
            />

            <StyledTablePagination
                component="div"
                count={items}
                rowsPerPageOptions={rowsPerPageOptions}
                // PAGE
                page={page - 1}
                onChangePage={onChangePage}
                // ROWS
                rowsPerPage={rowsPerPage}
                onChangeRowsPerPage={onChangeRowsPerPage}
                // LABELS
                labelRowsPerPage=""
                labelDisplayedRows={({ from, to, count }) =>
                    labelDisplayedRows || `${from}-${to} of ${count} ${type}`
                }
                SelectProps={{
                    IconComponent: (props) => <ArrowLeftIcon {...props} />,
                }}
            />
        </div>
    );
}

const StyledPagination = withStyles({
    root: {
        '&.MuiPaginationItem-icon': {
            fontSize: '18px',
        },
    },
})(Pagination);

const StyledTablePagination = withStyles({
    root: {
        fontFamily: 'CircularStd',
    },
    actions: {
        display: 'none',
    },
    toolbar: {
        flexDirection: 'row-reverse',
    },
    select: {
        border: `1px solid ${theme.colors.grey300}`,
        color: theme.colors.grey300,
        fontFamily: 'CircularStd',
        fontSize: '9px',
        fontWeight: '500',
        textTransform: 'uppercase',
        lineHeight: '11px',
        padding: '5px 16px',
    },
    menuItem: {
        fontFamily: 'CircularStd',
        fontSize: '9px',
        fontWeight: '500',
        padding: '5px 16px',

        '&.Mui-selected': {
            backgroundColor: theme.colors.green600,
            color: theme.colors.white,
        },

        '&.Mui-selected:hover': {
            backgroundColor: theme.colors.green600,
            color: theme.colors.white,
        },
    },
    selectIcon: {
        height: '10px',
        marginTop: '3px',
        top: 'initial',
        width: '20px',
    },
})(TablePagination);
